import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import Div from "@jumbo/shared/Div";
import { Grid, Typography, Box } from "@mui/material";
import React from "react";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import CodeIcon from "@mui/icons-material/Code";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import TerminalIcon from "@mui/icons-material/Terminal";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";

const features = [
  {
    icon: <AcUnitIcon />,
    title: "Visualize Everything",
    description:
      "Every concept and problem on CodingPal is visualizable. Our visualization tab lets you see concepts in action, making complex coding topics easy to understand.",
    gradient: "linear-gradient(135deg, #7C4DFF 0%, #B388FF 100%)",
  },
  {
    icon: <FolderCopyIcon />,
    title: "From Scratch",
    description:
      "New to coding? Our detailed, level-based tutorials guide you step-by-step to master coding concepts, perfect for beginners.",
    gradient: "linear-gradient(135deg, #FF4081 0%, #F8BBD0 100%)",
  },
  {
    icon: <CodeIcon />,
    title: "Learn Once, Implement Anywhere",
    description:
      "Our object-oriented approach helps you understand concepts clearly and implement them in any programming language of your choice.",
    gradient: "linear-gradient(135deg, #FF5252 0%, #FF8A80 100%)",
  },
  {
    icon: <LibraryBooksIcon />,
    title: "In-Depth Explanations",
    description:
      "Every concept comes with detailed explanations and visualizations, ensuring you retain knowledge for the long term.",
    gradient: "linear-gradient(135deg, #4CAF50 0%, #A5D6A7 100%)",
  },
  {
    icon: <DesignServicesIcon />,
    title: "Multiple Approaches",
    description:
      "Explore various solutions to interview problems with detailed implementations and visualizations for each approach.",
    gradient: "linear-gradient(135deg, #8D6E63 0%, #BCAAA4 100%)",
  },
  {
    icon: <AutoGraphIcon />,
    title: "Time and Space Complexities",
    description:
      "Understand time and space complexities with clear explanations and visualizations to prepare for technical interviews.",
    gradient: "linear-gradient(135deg, #90A4AE 0%, #CFD8DC 100%)",
  },
  {
    icon: <TerminalIcon />,
    title: "Code Snippets",
    description:
      "Access code snippets for all approaches in JavaScript, Java, and Python, ready for reference anytime.",
    gradient: "linear-gradient(135deg, #0288D1 0%, #4FC3F7 100%)",
  },
  {
    icon: <FormatListNumberedIcon />,
    title: "Dedicated Tests",
    description:
      "Test solutions with dedicated test sets to understand behavior across various inputs.",
    gradient: "linear-gradient(135deg, #AB47BC 0%, #E1BEE7 100%)",
  },
];

const FeaturesSection = () => {
  return (
    <Box
      sx={{
        bgcolor: "#F5F7FA",
      }}
    >
      <Grid container spacing={3}>
        <Grid
          item
          xs={12}
          sx={{
            textAlign: "center",
          }}
        >
          <Typography
            variant="h2"
            fontWeight={700}
            fontSize={{ xs: 28, md: 40 }}
            color="text.primary"
            sx={{ mb: 2 }}
          >
            Why Choose CodingPal?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto", fontSize: { xs: 16, md: 18 } }}
          >
            Discover powerful features designed to help you master Data
            Structures and Algorithms with ease.
          </Typography>
        </Grid>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} lg={3} key={index}>
            <JumboCardQuick
              sx={{
                bgcolor: "common.white",
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                height: "100%",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                },
                "& .MuiCardContent-root": {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  p: 3,
                  height: "100%",
                },
              }}
            >
              <Div
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: feature.gradient,
                  mb: 2,
                }}
              >
                {React.cloneElement(feature.icon, {
                  sx: { fontSize: 32, color: "common.white" },
                })}
              </Div>
              <Typography
                variant="h5"
                color="text.primary"
                fontWeight={600}
                sx={{ mb: 1, fontSize: { xs: 18, md: 20 } }}
              >
                {feature.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.6, fontSize: { xs: 14, md: 15 } }}
              >
                {feature.description}
              </Typography>
            </JumboCardQuick>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default FeaturesSection;
